import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { AnimatePresence, motion, useReducedMotion, type Transition } from 'motion/react';
import type { GitGraphProps } from '../contracts';
import type { GitCommit, GitRef, GitState } from '../../types';
import {
  NODE_R,
  PAD_TOP,
  ROW,
  edgeLength,
  edgePath,
  labelXFor,
  laneColor,
  laneX,
  layout,
  pillWidth,
  rowY,
  subjectBudget,
  truncate,
} from '../../graph';
import { ease, prefersReducedMotion, spring } from '../../lib/motion';
import { shortDate } from '../../lib/format';
import { sound } from '../../lib/sound';
import { useStrings } from '../../state/useLocale';
import { useTheme } from '../../state/useTheme';
import type { Strings } from '../../lib/strings';

/**
 * The live commit graph.
 *
 * This is the signature of the product, so the one rule it obeys is: history
 * moves, it never blinks. Every commit is a <motion.g> keyed by its hash and
 * positioned by an animated translation, with its children drawn around a
 * local origin. React keeps the identity across a rebase, a reset or an
 * amend, so the commits spring to their new rows and lanes and you can watch
 * where your work went.
 *
 * Two things resist that treatment. Edge paths cannot spring — a path string
 * is interpolated, not simulated — so they take a short tween. And a commit
 * that genuinely did not exist before has nowhere to travel from, so it
 * arrives instead, in the three beats motion.css (section 4) lays down: the
 * node drops into its lane, the edge draws from it to its parent, and only
 * then does HEAD slide across. Those beats are CSS, driven by classes this
 * file sets, because they are timed against one another and framer has no
 * part in them. HEAD itself — the white ring and the green pill — is one
 * element each, positioned by a CSS transform outside any row, so on a
 * checkout it travels to the commit you moved to and on a landing it waits
 * its turn. The two systems never touch the same property of one element.
 */

const DEFAULT_WIDTH = 640;
const MIN_WIDTH = 360;
const INSTANT: Transition = { duration: 0 };
/** The unreachable node's slow breath: present, but no longer spoken for. */
const BREATH: Transition = { duration: 3, repeat: Infinity, ease: 'easeInOut' };
/** A landing row's labels fading in: the kit's --med, so they finish with the node. */
const ARRIVE: Transition = { ...ease, duration: 0.2 };
/**
 * How long a landing stays marked. The kit's beats end at 560ms (node 200,
 * edge 160 after it, HEAD 200 after that); the classes come off a little
 * later, so nothing is cut short and no finished dash lingers to mask a move.
 */
const LANDING_MS = 600;
const NOTHING_LANDING: ReadonlySet<string> = new Set();

interface Pill {
  text: string;
  kind: string;
}

interface PlacedPill extends Pill {
  x: number;
  w: number;
}

/** A row's pills laid out left to right, and where the subject starts after them. */
interface PillRow {
  placed: PlacedPill[];
  end: number;
}

/**
 * The pills on a row.
 *
 * All but one are names the machine wrote — `HEAD`, `main`, `origin/main`,
 * `HEAD → main` — and they stay in Latin in both languages, arrow included,
 * because they are what the learner types and what `git log` prints back
 * (docs/i18n.md §5). `unreachable` is the one word here an author chose, so
 * it is the one that comes from the catalogue.
 */
function pillsFor(
  commit: GitCommit,
  refs: GitRef[],
  head: GitState['head'],
  isHead: boolean,
  s: Strings['play']['graph'],
): Pill[] {
  const pills: Pill[] = [];
  if (head.detached && isHead) pills.push({ text: 'HEAD', kind: 'head-detached' });
  for (const r of refs) {
    const isCurrent = r.kind === 'branch' && head.branch === r.short;
    pills.push({ text: isCurrent ? `HEAD → ${r.short}` : r.short, kind: isCurrent ? 'current' : r.kind });
  }
  if (!commit.reachable) pills.push({ text: s.unreachable, kind: 'ghost' });
  return pills;
}

/**
 * Pills, then the subject, in absolute SVG x, so the label layer in a row can
 * simply cancel the lane offset — and so the one pill a row does not draw,
 * HEAD's, can be placed at the top level from the very same numbers.
 */
function placePills(pills: Pill[], from: number): PillRow {
  let x = from;
  const placed = pills.map((pill) => {
    const w = pillWidth(pill.text);
    const at = x;
    x += w + 6;
    return { ...pill, x: at, w };
  });
  return { placed, end: x };
}

/* ── One commit ──────────────────────────────────────────────────────────── */

interface CommitRowProps {
  commit: GitCommit;
  lane: number;
  row: number;
  width: number;
  pills: PillRow;
  isHead: boolean;
  isSelected: boolean;
  /** True while this commit lands: absent from the last render, or still in the beats after. */
  isLanding: boolean;
  reduced: boolean;
  onSelect(hash: string | null): void;
}

function CommitRow({
  commit,
  lane,
  row,
  width,
  pills,
  isHead,
  isSelected,
  isLanding,
  reduced,
  onSelect,
}: CommitRowProps) {
  const s = useStrings().play.graph;
  const { theme } = useTheme();
  const cx = laneX(lane);
  const cy = rowY(row);
  // The lane ramp is SVG fills, which cannot read a token, so this is the one
  // part of the drawing that has to know which ground it is on (web/src/graph.ts).
  const color = laneColor(lane, theme);
  const move: Transition = reduced ? INSTANT : spring;

  const className = ['commit', commit.reachable ? '' : 'unreachable', isHead ? 'head' : '', isSelected ? 'selected' : '']
    .filter(Boolean)
    .join(' ');

  function select() {
    sound.click();
    // Clicking the open commit closes it again.
    onSelect(isSelected ? null : commit.hash);
  }

  function onKeyDown(e: ReactKeyboardEvent<SVGGElement>) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    select();
  }

  const subject = truncate(commit.subject, subjectBudget(width, pills.end));
  // Through lib/format rather than graph.ts's own `toLocaleDateString`, which
  // asked the browser what language it felt like and so printed an English
  // month beside Arabic prose — and Arabic-Indic digits beside the Western
  // ones the terminal prints. `shortDate` pins both to the page's language.
  const date = commit.time ? shortDate(new Date(commit.time * 1000)) : '';

  return (
    <motion.g
      className={className}
      data-hash={commit.hash}
      tabIndex={0}
      role="button"
      aria-pressed={isSelected}
      aria-label={s.commitLabel(commit.short, commit.subject)}
      // The whole point: position is animated, children are drawn around
      // (0, 0), so the group interpolates to its new row and lane. A landing
      // row only fades in, on the node's own clock; the node does the arriving.
      initial={isLanding ? { x: cx, y: cy, opacity: 0 } : false}
      animate={{ x: cx, y: cy, opacity: 1 }}
      exit={{ opacity: 0, transition: reduced ? INSTANT : ease }}
      transition={reduced ? INSTANT : { ...spring, opacity: ARRIVE }}
      onClick={select}
      onKeyDown={onKeyDown}
    >
      {/* Everything that belongs to the row rather than the node: it rides the
          row's y but undoes the lane's x, on the same spring, so the labels
          stay pinned to the left edge while the node slides between lanes. */}
      <motion.g initial={false} animate={{ x: -cx }} transition={move}>
        <rect className="row-hit" x={0} y={-ROW / 2} width={width} height={ROW} />

        {/* HEAD's own pill keeps its place in the layout so the subject does
            not shift, but is drawn once, above the rows, where it can travel. */}
        {pills.placed.map((pill) =>
          pill.kind === 'current' ? null : (
            <g className={`ref ref-${pill.kind}`} key={`${pill.kind}:${pill.text}`}>
              <rect x={pill.x} y={-9} width={pill.w} height={18} rx={4} />
              <text x={pill.x + 7} y={4}>
                {pill.text}
              </text>
            </g>
          ),
        )}

        <text className="subject" x={pills.end} y={4}>
          {subject}
        </text>

        <text className="hash" x={width - 8} y={4} textAnchor="end">
          {commit.short}
          {date && (
            <tspan className="date" dx={6}>
              {date}
            </tspan>
          )}
        </text>
      </motion.g>

      {/* The node itself, around the group's local origin. A commit that did
          not exist before drops into its lane — the first beat, a CSS
          animation rather than a framer one so the edge and HEAD can be
          timed to follow it. Framer never animates this group. */}
      <g className={isLanding ? 'node is-landing' : 'node'}>
        {/* The knockout ring and the ghost's ground are the page's, not the
            lane's, so they are named in play.css and left off the element:
            a presentation attribute cannot resolve a token, and a class can. */}
        {commit.reachable ? (
          <circle className="node-fill" r={NODE_R} fill={color} strokeWidth={2} />
        ) : (
          <motion.circle
            className="node-ghost"
            r={NODE_R}
            stroke={color}
            strokeWidth={1.5}
            strokeDasharray="2 2"
            strokeOpacity={0.7}
            animate={reduced ? { opacity: 1 } : { opacity: [0.75, 1, 0.75] }}
            transition={reduced ? INSTANT : BREATH}
          />
        )}
      </g>
    </motion.g>
  );
}

/* ── The graph ───────────────────────────────────────────────────────────── */

interface Edge {
  key: string;
  child: string;
  d: string;
  len: number;
  color: string;
  opacity: number;
  dashed: boolean;
}

export function GitGraph({ git, selected, onSelect, emptyMessage }: GitGraphProps) {
  const s = useStrings().play.graph;
  // For the edges only; each row reads it again for its node. The edges are
  // memoised on the commit list, so the ramp has to be a dependency or a theme
  // switch would repaint every node and leave every line behind it.
  const { theme } = useTheme();
  const reduced = (useReducedMotion() ?? prefersReducedMotion()) === true;
  const [wrap, setWrap] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(DEFAULT_WIDTH);

  useEffect(() => {
    if (!wrap) return;
    const apply = (w: number) => setWidth(Math.max(MIN_WIDTH, Math.round(w) || DEFAULT_WIDTH));
    apply(wrap.clientWidth);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) apply(entry.contentRect.width);
    });
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [wrap]);

  const commits = git.commits;

  // "New" means: absent from the last render that reached the screen. The set
  // is written in an effect rather than during render so a double render in
  // StrictMode cannot swallow the arrival animation.
  const seen = useRef<Set<string> | null>(null);
  const previous = seen.current;
  useEffect(() => {
    seen.current = new Set(commits.map((c) => c.hash));
  }, [commits]);
  const isNew = (hash: string) => previous !== null && !previous.has(hash);

  // A landing outlives the render that noticed it. The classes that drive the
  // three beats stay on for LANDING_MS — across any re-render a poll or a
  // click causes meanwhile — and come off together, so a node is never caught
  // mid-drop and an edge never keeps a dash it has finished with. State, not
  // a ref, because taking them off has to be a render of its own.
  const fresh = commits
    .filter((c) => isNew(c.hash))
    .map((c) => c.hash)
    .join(' ');
  const [landing, setLanding] = useState(NOTHING_LANDING);
  useEffect(() => {
    if (fresh === '') return;
    setLanding((prev) => {
      const next = new Set(prev);
      for (const hash of fresh.split(' ')) next.add(hash);
      return next;
    });
  }, [fresh]);
  useEffect(() => {
    if (landing.size === 0) return;
    const timer = window.setTimeout(() => setLanding(NOTHING_LANDING), LANDING_MS);
    return () => window.clearTimeout(timer);
  }, [landing]);
  const isLanding = (hash: string) => isNew(hash) || landing.has(hash);
  const anyLanding = fresh !== '' || landing.size > 0;

  const { pos, laneCount } = useMemo(() => layout(commits), [commits]);

  const refsByHash = useMemo(() => {
    const map = new Map<string, GitRef[]>();
    for (const r of git.refs) {
      if (r.kind === 'other') continue;
      const list = map.get(r.hash) ?? [];
      list.push(r);
      map.set(r.hash, list);
    }
    return map;
  }, [git.refs]);

  const labelX = labelXFor(laneCount);
  const height = PAD_TOP + commits.length * ROW;
  const headPos = git.head.hash ? pos.get(git.head.hash) : undefined;

  // Every row's pills, laid out here rather than in the row, because HEAD's
  // pill is drawn at the top level from the same numbers.
  const pills = new Map(
    commits.map((c): [string, PillRow] => [
      c.hash,
      placePills(pillsFor(c, refsByHash.get(c.hash) ?? [], git.head, git.head.hash === c.hash, s), labelX),
    ]),
  );
  const headPill = git.head.hash ? pills.get(git.head.hash)?.placed.find((p) => p.kind === 'current') : undefined;

  // Child → parent edges, in SVG space, beneath the nodes.
  const edges = useMemo(() => {
    const list: Edge[] = [];
    for (const c of commits) {
      const from = pos.get(c.hash);
      if (!from) continue;
      for (const p of c.parents) {
        const to = pos.get(p);
        if (!to) continue;
        const x1 = laneX(from.lane);
        const y1 = rowY(from.row);
        const x2 = laneX(to.lane);
        const y2 = rowY(to.row);
        list.push({
          key: `${c.hash}:${p}`,
          child: c.hash,
          d: edgePath(x1, y1, x2, y2),
          len: edgeLength(x1, y1, x2, y2),
          // The parent's lane owns the line, so a merge leans into the branch
          // it came from rather than repainting it.
          color: laneColor(to.lane, theme),
          opacity: c.reachable ? 1 : 0.45,
          dashed: !c.reachable,
        });
      }
    }
    return list;
  }, [commits, pos, theme]);

  if (!git.isRepo) {
    return <div className="graph-empty">{splitLead(emptyMessage ?? s.noRepo)}</div>;
  }
  if (commits.length === 0) {
    return <div className="graph-empty">{splitLead(s.noCommits)}</div>;
  }

  const edgeMove: Transition = reduced ? INSTANT : ease;

  return (
    // The graph stays left to right in both languages (docs/i18n.md §7). It
    // is drawn in SVG user space from x=0 rightwards — lanes, edge paths and
    // the hash column are all absolute numbers — and everything written in it
    // is a hash, a ref or a subject the machine holds. `direction` inherits
    // into SVG text, so this is also what keeps the ref pills reading the way
    // `git log` prints them.
    <div ref={setWrap} dir="ltr">
      {/*
        Deliberately no viewBox. With one, the element's layout width and the
        viewBox width are two numbers that must agree, and the instant they do
        not the browser silently scales the entire graph: commit rows bunch up
        and the type shrinks, which is easy to miss and impossible to explain.
        Without it, user units are CSS pixels no matter what the measurement
        says, so a stale width overflows into the pane's own scroll instead of
        quietly resizing the drawing.
      */}
      <svg
        className={anyLanding ? 'git-graph is-landing' : 'git-graph'}
        width={width}
        height={height}
        role="group"
        aria-label={s.label}
      >
        <g>
          <AnimatePresence initial={false}>
            {edges.map((e) => {
              // The second beat. A reachable edge whose child is landing is
              // dashed to its own length and drawn in from the child down to
              // the parent, which replaces framer's fade. A dashed edge has no
              // dash to spare for that, so it still fades in.
              const drawing = isLanding(e.child) && !e.dashed;
              return (
                <motion.path
                  key={e.key}
                  className={drawing ? 'edge is-drawing' : 'edge'}
                  style={drawing ? ({ '--len': e.len } as CSSProperties) : undefined}
                  fill="none"
                  stroke={e.color}
                  strokeWidth={2}
                  strokeDasharray={e.dashed ? '4 3' : undefined}
                  initial={e.dashed && isLanding(e.child) ? { d: e.d, opacity: 0 } : false}
                  animate={{ d: e.d, opacity: e.opacity }}
                  exit={{ opacity: 0, transition: edgeMove }}
                  transition={edgeMove}
                />
              );
            })}
          </AnimatePresence>
        </g>

        <AnimatePresence initial={false}>
          {commits.map((c) => {
            const p = pos.get(c.hash)!;
            return (
              <CommitRow
                key={c.hash}
                commit={c}
                lane={p.lane}
                row={p.row}
                width={width}
                pills={pills.get(c.hash)!}
                isHead={git.head.hash === c.hash}
                isSelected={selected === c.hash}
                isLanding={isLanding(c.hash)}
                reduced={reduced}
                onSelect={onSelect}
              />
            );
          })}
        </AnimatePresence>

        {/* HEAD lives above the rows and outside any of them: the white ring
            around the commit you are on, and the green pill naming its
            branch. Each is a single element placed by a CSS transform, drawn
            around (0, 0), so a checkout or a reset shows as HEAD travelling to
            where you landed instead of one HEAD vanishing and another
            appearing — and the stylesheet can hold that slide back until a
            landing commit has settled, the third beat. Clicks fall through to
            the row beneath. On first paint there is no earlier transform to
            transition from, so a fresh graph places HEAD without a slide. */}
        {headPos && (
          <circle
            className="head-ring"
            r={NODE_R + 4}
            fill="none"
            strokeWidth={1.5}
            opacity={0.9}
            pointerEvents="none"
            style={{ transform: `translate(${laneX(headPos.lane)}px, ${rowY(headPos.row)}px)` }}
          />
        )}
        {headPos && headPill && (
          <g
            className="ref ref-current"
            pointerEvents="none"
            style={{ transform: `translate(${headPill.x}px, ${rowY(headPos.row)}px)` }}
          >
            <rect x={0} y={-9} width={headPill.w} height={18} rx={4} />
            <text x={7} y={4}>
              {headPill.text}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}

/**
 * The empty-state copy arrives as plain text and is rendered as plain text.
 * The first sentence carries the headline weight the stylesheet expects; the
 * rest follows as its own line.
 */
function splitLead(text: string) {
  const stop = text.indexOf('.');
  if (stop === -1) return <strong>{text}</strong>;
  const lead = text.slice(0, stop + 1);
  const rest = text.slice(stop + 1).trim();
  return (
    <>
      <strong>{lead}</strong>
      {rest || null}
    </>
  );
}

export default GitGraph;
